/**
 * 📦 MAX CHAT WIDGET - Chatbot Support Livraison
 * 
 * Widget de chat avec Max pour aider les clients
 * avec les commandes et livraisons.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, Send, X, Truck, ChevronDown, 
  Loader2, HelpCircle, Phone, Package
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Avatar de Max le livreur
const MAX_AVATAR = 'https://static.prod-images.emergentagent.com/jobs/90b8cfd5-4d07-402e-9a12-a0e9be89c0f2/images/d2dd79574adadc4218e7bf647409093e50bc748604d8f603eea8192b674d4867.png';

// Questions rapides suggérées
const QUICK_QUESTIONS = [
  "Comment commander ?",
  "Zones de livraison",
  "Frais de livraison",
  "Moyens de paiement",
  "Ma livraison est en retard"
];

/**
 * 📦 MaxChatWidget - Widget de chat avec Max
 */
export function MaxChatWidget({ 
  customerId = null,
  position = 'bottom-right', // bottom-right, bottom-left, inline
  defaultOpen = false,
  isDarkMode = true,
  compact = false
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Message d'accueil initial
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'bot',
        content: "Salut ! 👋 Moi c'est Max, ton assistant livraison ! 📦\n\nComment je peux t'aider aujourd'hui ? Tu veux passer commande, savoir si on livre chez toi, ou autre chose ?",
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Envoyer un message
  const handleSendMessage = async (messageText = null) => {
    const text = messageText || newMessage.trim();
    if (!text || sending) return;
    
    // Ajouter le message utilisateur
    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setSending(true);
    
    try {
      const response = await fetch(`${API_URL}/api/max/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: customerId,
          sessionId: sessionId,
          message: text
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSessionId(data.sessionId);
        setMessages(prev => [...prev, {
          role: 'bot',
          content: data.response,
          timestamp: data.timestamp
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'bot',
          content: "Oups, j'ai eu un petit souci 😅 Essaie de reformuler ou appelle le 07 80 64 89 76 !",
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (err) {
      console.error('Erreur Max chat:', err);
      setMessages(prev => [...prev, {
        role: 'bot',
        content: "Hmm, problème de connexion 😕 Appelle directement le 07 80 64 89 76, l'équipe t'aidera !",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setSending(false);
    }
  };

  // Position du widget
  const positionClasses = {
    'bottom-right': 'fixed bottom-4 right-4 z-50',
    'bottom-left': 'fixed bottom-4 left-4 z-50',
    'inline': 'relative'
  };

  // Version compacte (bulle flottante)
  if (!isOpen && position !== 'inline') {
    return (
      <motion.button
        onClick={(e) => {
          e.stopPropagation(); // Empêche la fermeture du modal parent
          setIsOpen(true);
        }}
        className={`${positionClasses[position]} w-16 h-16 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform overflow-hidden border-3 border-orange-400`}
        style={{
          boxShadow: '0 0 20px rgba(255, 165, 0, 0.5), 0 4px 15px rgba(0,0,0,0.3)'
        }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        data-testid="max-chat-button"
      >
        <img 
          src={MAX_AVATAR} 
          alt="Max le livreur" 
          className="w-full h-full object-cover"
        />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
      </motion.button>
    );
  }

  return (
    <motion.div
      className={`${position !== 'inline' ? positionClasses[position] : ''} ${compact ? 'w-80' : 'w-96'} max-w-[calc(100vw-2rem)]`}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      onClick={(e) => e.stopPropagation()} // Empêche la fermeture du modal parent
    >
      <div className={`rounded-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/30 shadow-lg">
            <img 
              src={MAX_AVATAR} 
              alt="Max" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-white">Max</h3>
            <p className="text-xs text-white/80">Assistant Livraison 🛵</p>
          </div>
          {position !== 'inline' && (
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-full hover:bg-white/20 text-white transition-colors"
              data-testid="max-close-button"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {/* Messages */}
        <div 
          className={`${compact ? 'h-64' : 'h-80'} overflow-y-auto p-4 space-y-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}
          style={{ scrollBehavior: 'smooth' }}
        >
          {messages.map((msg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              {msg.role === 'bot' && (
                <div className="w-8 h-8 rounded-full overflow-hidden border border-orange-400 flex-shrink-0">
                  <img src={MAX_AVATAR} alt="Max" className="w-full h-full object-cover" />
                </div>
              )}
              
              <div className={`max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                <div className={`inline-block px-4 py-2 rounded-2xl text-sm ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
                    : isDarkMode 
                      ? 'bg-gray-800 text-gray-200 border border-gray-700' 
                      : 'bg-white text-gray-800 shadow-sm'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            </motion.div>
          ))}
          
          {sending && (
            <div className="flex gap-2">
              <div className="w-8 h-8 rounded-full overflow-hidden border border-orange-400 flex-shrink-0">
                <img src={MAX_AVATAR} alt="Max" className="w-full h-full object-cover" />
              </div>
              <div className={`px-4 py-2 rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white shadow-sm'}`}>
                <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Questions rapides */}
        {messages.length <= 2 && (
          <div className={`px-4 pb-2 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Questions fréquentes :
            </p>
            <div className="flex flex-wrap gap-1">
              {QUICK_QUESTIONS.map((q, i) => (
                <button
                  key={i}
                  onClick={() => handleSendMessage(q)}
                  disabled={sending}
                  className={`text-xs px-2 py-1 rounded-full transition-all ${
                    isDarkMode 
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700' 
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Input */}
        <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-2">
            <div className={`flex-1 flex items-center gap-2 px-4 py-2 rounded-full ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                placeholder="Pose ta question..."
                className={`flex-1 bg-transparent outline-none text-sm ${
                  isDarkMode ? 'text-white placeholder-gray-400' : 'text-gray-800 placeholder-gray-500'
                }`}
                disabled={sending}
                data-testid="max-message-input"
              />
            </div>
            
            <motion.button
              onClick={() => handleSendMessage()}
              disabled={!newMessage.trim() || sending}
              className={`p-3 rounded-full ${
                newMessage.trim() && !sending
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg'
                  : isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="max-send-button"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </motion.button>
          </div>
          
          {/* Lien téléphone */}
          <div className="mt-2 text-center">
            <a 
              href="tel:0780648976"
              className={`text-xs inline-flex items-center gap-1 ${isDarkMode ? 'text-gray-400 hover:text-orange-400' : 'text-gray-500 hover:text-orange-500'}`}
            >
              <Phone className="w-3 h-3" />
              Besoin d'aide ? 07 80 64 89 76
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * 📦 MaxChatInline - Version intégrée (non-flottante)
 */
export function MaxChatInline({ customerId, isDarkMode = true }) {
  return (
    <MaxChatWidget 
      customerId={customerId}
      position="inline"
      defaultOpen={true}
      isDarkMode={isDarkMode}
    />
  );
}

export default MaxChatWidget;
